import { useEffect, useState } from 'react';
import Markdown from 'markdown-to-jsx';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import styled from 'styled-components';

const MarkdownContent = styled.div`
  color: #333;
  text-align: left;

  h1:first-child {
    display: none;
  }

  h2 {
    color: #202124;
    font-size: 1.15rem;
    margin: 24px 0 8px;
  }

  h2:first-of-type {
    margin-top: 0;
  }

  p,
  li {
    font-size: 0.95rem;
    line-height: 1.55;
  }

  ul {
    margin: 0;
    padding-left: 22px;
  }

  li + li {
    margin-top: 6px;
  }
`;

export interface ChangelogDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ChangelogDialog = ({ open, onClose }: ChangelogDialogProps) => {
  const [changelog, setChangelog] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const versionQuery = process.env.REACT_APP_VERSION
    ? `?v=${encodeURIComponent(process.env.REACT_APP_VERSION)}`
    : '';

  useEffect(() => {
    if (!open) {
      return;
    }

    const controller = new AbortController();
    setLoadError(false);
    setChangelog(null);

    fetch(`${process.env.PUBLIC_URL}/CHANGELOG.md${versionQuery}`, {
      cache: 'no-store',
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load changelog (${response.status})`);
        }
        return response.text();
      })
      .then(setChangelog)
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setLoadError(true);
        }
      });

    return () => controller.abort();
  }, [open, versionQuery]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="changelog-dialog-title"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        id="changelog-dialog-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        What's new
        <IconButton aria-label="Close changelog" onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#fbfbfb' }}>
        {changelog === null && !loadError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress aria-label="Loading changelog" size={32} />
          </Box>
        )}
        {loadError && (
          <Alert severity="error">
            The changelog couldn't be loaded. Please try again later.
          </Alert>
        )}
        {changelog !== null && (
          <MarkdownContent>
            <Markdown options={{ disableParsingRawHTML: true }}>{changelog}</Markdown>
          </MarkdownContent>
        )}
      </DialogContent>
    </Dialog>
  );
};
